/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { twMerge } from 'utilities/src/twMerge';
import omit from 'lodash/omit';

import Button from '../Button';

import { SnackbarVariantsEnum, SnackbarSizesEnum } from './_props';
import type { SnackbarProps, SnackbarStatesType } from './_props';

import useSnackbarTheme from './themes';

import { useSnackbar } from './hooks/useSnackbar';

/**
 * Snackbar Component
 */

function Snackbar({
  children,
  state,
  variant = 'Primary',
  size = 'Default',
  className = '',
  float = false,
  primaryButtonProps,
  secondaryButtonProps,
  customButton, // TODO remove this and use appropriate primaryButtonProps for site and blog
  fadeOutTiming,
}: SnackbarProps) {
  const { display, visible } = useSnackbar({ state, fadeOutTiming, float });

  const {
    snackbarActionClasses,
    snackbarAnimationClassesMap,
    snackbarClasses,
    snackbarContainerClasses,
    snackbarContainerSizeClassesMap,
    snackbarContentClasses,
    snackbarContentVariantClassesMap,
    snackbarFloatClasses,
    snackbarSizeClassesMap,
    snackbarVariantClassesMap,
    snackbarVisibilityClasses,
    snackbarClassesEach,
    snackbarContainerClassesTheme,
  } = useSnackbarTheme();

  if (!display || !state) return null;

  const snackbarAnimationClasses = snackbarAnimationClassesMap.get(visible) as string;

  const snackbarVariantClasses = snackbarVariantClassesMap.get(SnackbarVariantsEnum[variant]);
  const snackbarSizeClasses = snackbarSizeClassesMap.get(SnackbarSizesEnum[size]);

  const snackbarContainerSizeClasses = snackbarContainerSizeClassesMap.get(SnackbarSizesEnum[size]);
  const snackbarContentVariantClasses = snackbarContentVariantClassesMap.get(SnackbarVariantsEnum[variant]);

  const secondaryButtonVariant = variant === 'Primary' ? 'WhiteOutlined' : 'PrimaryOutlined';

  /**
   * Render
   */

  return (
    <output
      className={twMerge(
        snackbarClassesEach,
        snackbarClasses,
        snackbarVariantClasses,
        snackbarSizeClasses,
        className,
        {
          [snackbarFloatClasses]: float,
          [snackbarVisibilityClasses]: float,
          [snackbarAnimationClasses]: float,
        },
      )}
    >
      <div
        className={twMerge(snackbarContainerClasses, snackbarContainerSizeClasses, snackbarContainerClassesTheme)}
      >
        <div className={twMerge(snackbarContentClasses, snackbarContentVariantClasses)}>{children}</div>

        <div className={twMerge(snackbarActionClasses)}>
          {secondaryButtonProps?.label && (
            <Button
              {...omit(secondaryButtonProps, 'label')}
              iconSize="IconS"
              variant={secondaryButtonVariant}
              type="button"
              size="Medium"
            >
              {secondaryButtonProps.label}
            </Button>
          )}

          {primaryButtonProps?.label && (
            <Button
              {...omit(primaryButtonProps, 'label')}
              iconSize="IconS"
              variant="Primary"
              type="button"
              size="Medium"
            >
              {primaryButtonProps.label}
            </Button>
          )}

          {customButton && customButton}
        </div>
      </div>
    </output>
  );
}

export default Snackbar;

export type { SnackbarStatesType, SnackbarProps };
